import { isBefore } from "date-fns";
import { z } from "zod";

import { OFFER_TYPES } from "../../../../constants";
import { formatNumber } from "../../../../utils";
import {
  deliveryCostIssue,
  isDemandLine,
  requiresDeliveryCost,
} from "../../../../utils/delivery-cost";
import type { DeliveryLocationOption } from "../../../DirectOfferDetails/schema";
import type { Offer } from "../OfferCard/interfaces";

// Quantities are capped at 99999.99999 by the negotiation API.
const MAX_QUANTITY = 99999.99999;

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const toNumber = (value: unknown) =>
  value === "" || value === undefined || value === null ? undefined : Number(value);

// Ports the legacy redux-form `validate`. The deposit-quantity ceiling only
// applies to direct offers, and only once the full deposit has loaded. A demand
// line has no delivery cost of its own (the demand's is negotiated apart).
export const createCounterOfferSchema = (offer: Offer, t: TranslateFn) => {
  const required = t("traderDetails.form.mandatoryFieldBlankError");
  const tooLarge = t("marketOfferDetails.form.quantityIsTooLarge");
  const isDirectOffer = offer.offer_type === OFFER_TYPES.DIRECT_OFFER;
  const depositQuantity = offer.deposit?.quantity;
  const demandLine = isDemandLine(offer);

  return z
    .object({
      minimum_quantity: z.preprocess(
        toNumber,
        z
          .number({ error: required })
          .max(MAX_QUANTITY, tooLarge)
          .refine(
            value =>
              !isDirectOffer || depositQuantity === undefined || value <= Number(depositQuantity),
            {
              message: t("counterOfferModal.validation.cantBeMoreThanOriginalDepositQuantity", {
                quantity: formatNumber(depositQuantity ?? 0),
                unit: offer.unit_of_measure?.code,
              }),
            }
          )
      ),
      // Optional and hidden for direct offers (the API copies it from the minimum).
      maximum_quantity: z.preprocess(toNumber, z.number().max(MAX_QUANTITY, tooLarge).optional()),
      price: z.preprocess(toNumber, z.number({ error: required })),
      delivery_location: z.custom<DeliveryLocationOption>(
        value => !!value && typeof value === "object" && "value" in value,
        { message: required }
      ),
      planned_exchange_time: z
        .date({ error: required })
        .refine(date => !isBefore(date, new Date()), {
          message: t("counterOfferModal.validation.incorrectExchangeDateOrTime"),
        }),
      delivery_cost: z.string().optional(),
      text: z.string().optional(),
    })
    .superRefine(
      (values, ctx) => {
        if (demandLine || !requiresDeliveryCost(values?.delivery_location?.value)) {
          return;
        }
        const issue = deliveryCostIssue(values.delivery_cost, t);
        if (issue) {
          ctx.addIssue({ code: "custom", path: ["delivery_cost"], message: issue });
        }
      },
      { when: () => true }
    );
};

export type CounterOfferFormValues = {
  minimum_quantity: string;
  maximum_quantity: string;
  price: string;
  delivery_cost: string;
  delivery_location: DeliveryLocationOption | null;
  planned_exchange_time: Date | null;
  text: string;
};
